import { TALLY_FIELDS, type TallyField } from "./reconciler_tallies";

/** One recovered entry mode and how many charges resolved to it. */
export interface EntryModeCount {
  mode: string;
  count: number;
}

/**
 * Pulls the `entryMode.<mode>` counters out of a Job's flat counter map.
 *
 * The entry-mode sweep reports what each charge resolved to alongside the usual
 * per-reconciler tally. `groupCounters` deliberately drops anything that is not
 * a tally field, so this is the only place those numbers surface — and they are
 * the whole point of the read-only audit.
 */
export function entryModeTally(raw: string | null): EntryModeCount[] {
  if (!raw) return [];

  let parsed: Record<string, unknown>;
  try {
    const value: unknown = JSON.parse(raw);
    if (!value || typeof value !== "object" || Array.isArray(value)) return [];
    parsed = value as Record<string, unknown>;
  } catch {
    return [];
  }

  const counts: EntryModeCount[] = [];
  for (const [key, value] of Object.entries(parsed)) {
    if (typeof value !== "number") continue;
    if (!key.startsWith("entryMode.")) continue;

    const mode = key.slice("entryMode.".length);
    // A reconciler named `entryMode` would report `entryMode.scanned` etc.
    // Those belong to the tally table; only real modes are listed here.
    if (TALLY_FIELDS.includes(mode as TallyField)) continue;

    counts.push({ mode, count: value });
  }

  return counts.sort(
    (a, b) => b.count - a.count || a.mode.localeCompare(b.mode),
  );
}

/**
 * Splits pasted merchant ids on any run of whitespace or commas.
 *
 * Deduplicated because the scope drives one query per id, and a list pasted from
 * a spreadsheet routinely repeats them.
 */
export function parseBusinessIds(raw: string): string[] {
  const seen = new Set<string>();
  for (const token of raw.split(/[\s,]+/)) {
    if (token) seen.add(token);
  }
  return [...seen];
}
